import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Routes, Route } from "react-router";
import { isEmpty } from "lodash";

import { NOOP } from "core/constants";
import { useAppState } from "core/contexts/AppContext";
import { CartItemsContextProvider } from "core/contexts/CartItemsContext";
import { EventRegistrationContextProvider } from "core/contexts/EventRegistrationContext";
import { getURLSegment } from "core/includes/formatters";
import { hasFeatureFlag } from "core/includes/site";
import { FLAG_SHOP_2_WITH_EVENTS, FLAG_SHOP_ONLINE_VIDEOS } from "core/includes/featureFlags";
import Footer from "core/Components/Footer";
import Header from "core/Components/Header";
import CartPopup from "core/Components/CartPopup/CartPopup";
import ConfirmationPopup from "pages/Components/CartAndCheckout/components/ConfirmationPopup";
import CartPage from "pages/Components/CartAndCheckout/CartPage";
import StripeLoader from "core/Components/Utils/StripeLoader";
import CheckoutPage from "pages/Components/CartAndCheckout/CheckoutPage";
import EventRegistrationRouter from "pages/Components/EventRegistration/EventRegistrationRouter";
import AuthPage from "pages/Components/auth/AuthPage";
import Main from "pages/Main";
import useGoogleTagsIntegration from "core/hooks/useGoogleTagsIntegration";
import useHubspotIntegration from "core/hooks/useHubspotIntegration";
import LoginDialog from "pages/Components/auth/LoginDialog";
import { hasTokenExpired } from "core/includes/auth";
import window from 'core/includes/window';

type Props = {
    showBreadcrumb: boolean,
    breadcrumbHandler: (showBreadcrumb: boolean) => void,
    footerMenu?: { menu: [any], side: [any], bottom: [any] },
    footerMenuHandler: (pages: any) => void,
};

const TARGETS_TO_BE_DISABLED_CART_ICON = [
    '/cart-summary',
    '/checkout',
];

const AppPage: React.FC<Props> = (
    {
        showBreadcrumb,
        breadcrumbHandler = NOOP,
        footerMenu = undefined,
        footerMenuHandler = NOOP,
    }
) => {
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    const [appState, setAppState] = useAppState();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const stage = params.get("stage");
    const currentURLSegment = getURLSegment(location.pathname);

    const hasShopEventsFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_2_WITH_EVENTS);
    const hasShopOnlineVideosFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_ONLINE_VIDEOS);

    const TARGETS_TO_HIDE_HEADER_COUNTRY_DROPDOWN: any = useMemo(() => ({
        pages: [],
        groupUrlSegment: [
            '/cart-summary',
            '/checkout',
            '/auth/',
        ]
    }), []);

    useHubspotIntegration();
    useGoogleTagsIntegration();

    useEffect(() => {
        const checkForTokenExpire = () => {
            const shouldOpenLoginDialog = hasTokenExpired() && !isEmpty(appState.user);
            if (shouldOpenLoginDialog) {
                setLoginDialogOpen(true);
            }
        }

        window.addEventListener('focus', checkForTokenExpire, false);

        return () => {
            window.removeEventListener('focus', checkForTokenExpire, false);
        }
    });

    // Close login dialog if the token is not expired and user is set OR when the token is expire AND the user is not set
    useEffect(() => {
        if (loginDialogOpen &&
            ((!hasTokenExpired() && !isEmpty(appState.user)) || (hasTokenExpired() && !appState.user))
        ) {
            setLoginDialogOpen(false);
        }
    }, [appState, loginDialogOpen]);

    const onCloseLoginDialog = () => {
        setLoginDialogOpen(false)
        setAppState({
            authorised: false
        })
    };

    // The option to hide the header country dropdown
    const hideHeaderCountry = useMemo(() => {
        const currentPage = appState.pageClassNames?.find((pageClassName: PageClassName) => pageClassName.urlSegment === currentURLSegment);
        // Check by ClassName
        if (currentPage) {
            return !!TARGETS_TO_HIDE_HEADER_COUNTRY_DROPDOWN.pages.find(
                (className: string) => currentPage.className.endsWith(className)
            );
            // Check by URLSegment
        } else {
            return !!TARGETS_TO_HIDE_HEADER_COUNTRY_DROPDOWN.groupUrlSegment.find(
                (urlSegment: string) => location.pathname.indexOf(urlSegment) > -1
            );
        }
    }, [appState.pageClassNames, currentURLSegment, TARGETS_TO_HIDE_HEADER_COUNTRY_DROPDOWN.pages, TARGETS_TO_HIDE_HEADER_COUNTRY_DROPDOWN.groupUrlSegment, location.pathname]);

    const isDisabledCartIcon = useMemo(() => {
        return !!TARGETS_TO_BE_DISABLED_CART_ICON.find(
            (urlSegment: string) => location.pathname.indexOf(urlSegment) > -1
        );
    }, [location.pathname]);

    return (
        <div className="App">
            {loginDialogOpen && hasShopOnlineVideosFeatureFlag && (
                <LoginDialog onClose={onCloseLoginDialog} />
            )}
            <CartItemsContextProvider>
                <CartPopup showBreadcrumb={showBreadcrumb} hideHeaderCountry={hideHeaderCountry} />
                <Header
                    stage={stage}
                    footerMenuHandler={footerMenuHandler}
                    breadcrumbHandler={breadcrumbHandler}
                    hideHeaderCountry={hideHeaderCountry}
                    isDisabledCartIcon={isDisabledCartIcon}
                />
                <EventRegistrationContextProvider>
                    <Routes>
                        <Route
                            path={"/"}
                            element={
                                <Main
                                    stage={stage}
                                    showBreadcrumb={showBreadcrumb}
                                    hideHeaderCountry={hideHeaderCountry}
                                />
                            }
                        />
                        <Route
                            path={"/auth/*"}
                            element={
                                <AuthPage />
                            }
                        />
                        <Route
                            path={"*"}
                            element={
                                <Main
                                    stage={stage}
                                    showBreadcrumb={showBreadcrumb}
                                    hideHeaderCountry={hideHeaderCountry}
                                />
                            }
                        />
                        <Route
                            path={"/cart-summary"}
                            element={
                                <CartPage
                                    showBreadcrumb={showBreadcrumb}
                                    hideHeaderCountry={hideHeaderCountry}
                                />
                            }
                        />
                        <Route
                            path={"/checkout"}
                            element={
                                <StripeLoader>
                                    <CheckoutPage
                                        showBreadcrumb={showBreadcrumb}
                                        hideHeaderCountry={hideHeaderCountry}
                                    />
                                </StripeLoader>
                            }
                        />
                        {hasShopEventsFeatureFlag && (
                            <Route
                                path={"/event-registration/:eventId/*"}
                                element={
                                    <EventRegistrationRouter
                                        showBreadcrumb={showBreadcrumb}
                                        hideHeaderCountry={hideHeaderCountry}
                                    />
                                }
                            />
                        )}
                        {hasShopOnlineVideosFeatureFlag && (
                            <>
                                <Route
                                    path={"/online-event/:onlineEventUrlSegment"}
                                    element={
                                        <Main
                                            stage={stage}
                                            showBreadcrumb={showBreadcrumb}
                                            hideHeaderCountry={hideHeaderCountry}
                                            urlSegmentName={'onlineEventUrlSegment'}
                                        />
                                    }
                                />
                                <Route
                                    path={"/online-event/:onlineEventUrlSegment/:segmentedVideoUrlSegment"}
                                    element={
                                        <Main
                                            stage={stage}
                                            showBreadcrumb={showBreadcrumb}
                                            hideHeaderCountry={hideHeaderCountry}
                                            urlSegmentName={'onlineEventUrlSegment'}
                                        />
                                    }
                                />
                            </>
                        )}
                    </Routes>
                </EventRegistrationContextProvider>
                {!appState.loading && (
                    <Footer footerMenu={footerMenu} />
                )}
                {!isDisabledCartIcon && (
                    <ConfirmationPopup />
                )}
            </CartItemsContextProvider>

        </div>
    );
}

export default AppPage;
